import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import AchievementBadge from './AchievementBadge';
import { SUBJECT_PALETTE, orderedSubjectKeys } from '../constants';
import './ProfileView.css';
import './SettingsPage.css';

function ProfileView({ userData, onSaveSettings, onRestartOnboarding, onStartTutorial, onOpenUpgrade, onOpenContact, setView }) {

   const [formData, setFormData] = useState({
        name: '',
        phone: '',
        defaultTheme: 'light',
        displayName: ''
    });
    
    const [zenMode, setZenMode] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [saveStatus, setSaveStatus] = useState('');
    const [allPossibleAchievements, setAllPossibleAchievements] = useState([]);
    const [showGalleryModal, setShowGalleryModal] = useState(false);

    useEffect(() => {
        if (userData) {
            setFormData({
                name: userData.profile?.name || '',
                phone: userData.profile?.phone || '',
                defaultTheme: userData.profile?.preferences?.default_theme || 'light',
                displayName: userData.profile?.display_name || 'Counselor'
            });
            setZenMode(userData.profile?.zen_mode || false);
        }

        const fetchMasterList = async () => {
            const { data } = await supabase
                .from('achievements')
                .select('*')
                .order('subject_slug', { ascending: true })
                .order('chapter_index', { ascending: true });
            if (data) setAllPossibleAchievements(data);
        };
        fetchMasterList();
    }, [userData]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setIsDirty(true);
        setSaveStatus('');
    };

    const handleZenToggle = (newVal) => {
        setZenMode(newVal);
        // Auto-save Zen Mode immediately for better UX
        onSaveSettings({
            profile: { zen_mode: newVal }
        });
    };

    const handleSaveOffice = () => {
        setSaveStatus('saving');
        onSaveSettings({
            name: formData.name,
            phone: formData.phone,
            display_name: formData.displayName,
            preferences: { default_theme: formData.defaultTheme },
        });
        setTimeout(() => {
            setSaveStatus('saved');
            setIsDirty(false);
        }, 800);
    };

    const handleLogout = async () => { await supabase.auth.signOut(); };

    const getInitials = (name) => (name || 'C').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

    const topSubjectColor = useMemo(() => {
        const topSub = userData?.topSubject ? Object.entries(userData.topSubject).sort((a,b) => b[1] - a[1])[0]?.[0] : null;
        if (!topSub) return 'var(--accent-color-primary)';
        
        // Find color from constants
        const index = orderedSubjectKeys.indexOf(topSub);
        return index >= 0 ? SUBJECT_PALETTE[index] : 'var(--accent-color-primary)';
    }, [userData]);


    const plan = userData?.access_level === 'free' ? 'Free Trial' : 
                 userData?.access_level === 'cycle' ? 'Cycle Pass' : 'Lifetime Access';

    return (
        <div className="profile-view-container theme-aware">
            
{/* SECTION A: PERSONA CARD */}
            <div className="persona-card">
                <div className="persona-header-bg"></div>
                <div className="persona-content">
                    <div className="persona-identity-row">
                        <div className="identity-left">
                            <div className="avatar-large" style={{ 
                                backgroundColor: topSubjectColor, 
                                color: '#fff',
                                textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                border: '4px solid var(--card-bg)'
                            }}>
                                {getInitials(formData.displayName)}
                            </div>
                            <div className="identity-text">
                                <h2>{formData.displayName}</h2>
                                <span className="rank-label">{userData?.profile?.weekly_points || 0} Weekly Pts</span>
                            </div>
                        </div>
                        <div className="identity-right">
                            <div className="zen-toggle-wrapper">
                                <span className="material-symbols-outlined" style={{color: zenMode ? 'var(--accent-color-primary)' : 'var(--text-muted)'}}>self_improvement</span>
                                <span className="zen-label">Zen Mode</span>
                                <label className="toggle-switch">
                                    <input type="checkbox" checked={zenMode} onChange={(e) => handleZenToggle(e.target.checked)} />
                                    <span className="slider round"></span>
                                </label>
                            </div>
                        </div>
                    </div>

                    {!zenMode && (
                <div className="trophy-case-section">
                    <div className="trophy-case-header">
                        <span className="section-label">Your Achievements</span>
                        <button className="view-all-btn" onClick={() => setShowGalleryModal(true)}>
                            View Gallery <span className="material-symbols-outlined">arrow_forward</span>
                        </button>
                    </div>
                    <div className="trophy-grid earned-only">
                        {userData?.achievements && userData.achievements.length > 0 ? (
                            userData.achievements.map(ach => (
                                <div key={ach.id} className="trophy-item is-unlocked" title={ach.details?.name}>
                                    <AchievementBadge 
                                        subject={ach.details?.subject_slug || 'torts'} 
                                        chapterIndex={ach.details?.chapter_index || 1} 
                                        size={60}
                                        locked={false}
                                    />
                                    <div className="trophy-title-text">{ach.details?.name}</div>
                                </div>
                            ))
                        ) : (
                            <div className="empty-state-text">
                                Master a chapter to earn your first shield.
                            </div>
                        )}
                    </div>

                    {showGalleryModal && (
                        <div className="modal-overlay achievement-gallery-overlay" onClick={() => setShowGalleryModal(false)}>
                            <div className="gallery-modal theme-aware" onClick={e => e.stopPropagation()}>
                                <header className="gallery-modal-header">
                                    <div className="header-titles">
                                        <h2>Mastery Gallery</h2>
                                        <p>{(userData?.achievements || []).length} / {allPossibleAchievements.length} Shields Collected</p>
                                    </div>
                                    <button className="icon-btn close-btn" onClick={() => setShowGalleryModal(false)}>
                                        <span className="material-symbols-outlined">close</span>
                                    </button>
                                </header>
                                <div className="gallery-modal-body scrollable-grid">
                                    {allPossibleAchievements.map(ach => {
                                        const isUnlocked = (userData?.achievements || []).some(ua => ua.id === ach.id);
                                        return (
                                            <div 
                                                key={ach.id} 
                                                className={`gallery-item ${!isUnlocked ? 'is-locked' : 'is-unlocked'}`}
                                                title={isUnlocked ? `Mastered: ${ach.name}` : `Requirement: ${ach.description}`}
                                            >
                                                <AchievementBadge 
                                                    subject={ach.subject_slug} 
                                                    chapterIndex={ach.chapter_index} 
                                                    size={70}
                                                    locked={!isUnlocked}
                                                />
                                                <div className="gallery-item-text">
                                                    <div className="ach-name">{ach.name}</div>
                                                    {!isUnlocked && <div className="ach-status">Locked</div>}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
                </div>
            </div>

            {/* SECTION B: BACK OFFICE */}
            <div className="back-office-grid">
                
                {/* 1. Identity & Prefs */}
                <div className="office-card">
                    <div className="office-header"><span className="material-symbols-outlined">badge</span> Identity & Prefs</div>
                    
                    <div className="setting-col">
                        <label>Display Name (Public)</label>
                        <input type="text" value={formData.displayName} onChange={(e) => handleChange('displayName', e.target.value)} />
                    </div>
                    <div className="setting-col">
                        <label>Full Name (Billing)</label>
                        <input type="text" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} />
                    </div>
                    <div className="setting-col">
                        <label>App Theme</label>
                        <select value={formData.defaultTheme} onChange={(e) => handleChange('defaultTheme', e.target.value)}>
                            <option value="light">Light Mode</option>
                            <option value="dark">Dark Mode</option>
                        </select>
                    </div>
                    <button className="btn primary" onClick={handleSaveOffice} disabled={!isDirty}>
                        {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Save Changes'}
                    </button>
                </div>

                {/* 2. Account & Support */}
                <div className="office-card">
                    <div className="office-header"><span className="material-symbols-outlined">manage_accounts</span> Account Actions</div>
                    
                    <div className="setting-col">
                        <label>Membership Tier</label>
                        <div className="readonly-field">
                            <span className="material-symbols-outlined field-icon">verified</span>
                            <span className="field-text">{plan}</span>
                        </div>
                        {userData?.access_level === 'free' && (
                            <button className="text-btn danger" onClick={onOpenUpgrade} style={{textAlign:'center', marginTop:'5px'}}>Upgrade Now</button>
                        )}
                    </div>

                    <div className="setting-col">
                        <label>Strategy</label>
                        <button className="text-btn" onClick={onRestartOnboarding}><span className="material-symbols-outlined" style={{verticalAlign:'middle', marginRight:'8px'}}>tune</span> Reconfigure Plan</button>
                    </div>

                    <div className="setting-col">
                        <label>Support</label>
                        <button className="text-btn" onClick={onOpenContact}><span className="material-symbols-outlined" style={{verticalAlign:'middle', marginRight:'8px'}}>headset_mic</span> Contact Wali</button>
                    </div>

                    <button className="text-btn" onClick={handleLogout} style={{color: 'var(--text-muted)', marginTop: 'auto'}}>
                        <span className="material-symbols-outlined" style={{verticalAlign:'middle'}}>logout</span> Log Out
                    </button>
                </div>

            </div>
        </div>
    );
}

export default ProfileView;